import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, FlexProps, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { IssuerType } from '../../Utils/backStageApi';
import { getIssuerEffectivePeriod } from '../../Utils/common';
import { GenericTable } from '../Common/GenericTable';
import { TablePager } from '../Common/TablePager';

export interface IssuerItem extends IssuerType {}

interface Props extends Pick<FlexProps, 'mt'> {
  issuers: IssuerItem[];
  onEdit: (issuer: IssuerItem) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20] as const;

const TABLE_CELL_TEXT_STYLE = {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.01em',
  color: '#00292B',
};

export const IssuerTable: React.FC<Props> = ({ issuers, onEdit, mt }) => {
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const totalItems = issuers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = totalItems === 0 ? 0 : (safePage - 1) * pageSize;
  const paginated = issuers.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [issuers]);

  const columns = [
    { label: '機構名稱', width: '190px', paddingLeft: '40px' },
    { label: '機構代碼', width: '190px' },
    { label: 'IP', width: '170px' },
    { label: '方案', width: '130px' },
    { label: '生效時間', width: '260px' },
    { label: '', width: '100px' },
  ];

  const renderRow = (item: IssuerItem) => {
    const period = getIssuerEffectivePeriod(item.plan_start_date);

    return (
      <Flex key={item.id} w="1040px" minH="60px" align="stretch">
        <Flex w="190px" minW="190px" align="center" pl="40px" pr="12px" borderBottom="1px solid #00AAFF">
          <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.name}>
            {item.name}
          </Text>
        </Flex>

        <Flex w="190px" minW="190px" align="center" px="12px" borderBottom="1px solid #00AAFF">
          <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.id} fontFamily="monospace" fontSize="15px">
            {item.id}
          </Text>
        </Flex>

        <Flex w="170px" minW="170px" align="center" px="12px" borderBottom="1px solid #00AAFF">
          <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.ips.join(', ')}>
            {item.ips.length > 0 ? item.ips.join(', ') : '—'}
          </Text>
        </Flex>

        <Flex w="130px" minW="130px" align="center" px="12px" borderBottom="1px solid #00AAFF">
          <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.plan_name_zh}>
            {item.plan_name_zh}
          </Text>
        </Flex>

        <Flex w="260px" minW="260px" align="center" px="12px" borderBottom="1px solid #00AAFF">
          <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1}>
            {period ? `${period.start} ~ ${period.end}` : '尚未設定'}
          </Text>
        </Flex>

        <Flex w="100px" minW="100px" justify="center" align="center" px="12px" borderBottom="1px solid #00AAFF">
          <Button variant="unstyled" minW="37px" h="22px" onClick={() => onEdit(item)}>
            <Text
              fontFamily="'Open Sans', sans-serif"
              fontSize="18px"
              fontWeight={600}
              lineHeight="120%"
              letterSpacing="0.01em"
              textDecoration="underline"
              color="#006699"
            >
              編輯
            </Text>
          </Button>
        </Flex>
      </Flex>
    );
  };

  return (
    <Flex direction="column" w="1040px" mt={mt}>
      <GenericTable columns={columns} isEmpty={paginated.length === 0} emptyText="目前沒有機構資料">
        {paginated.map(renderRow)}
      </GenericTable>

      <TablePager
        mt="50px"
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={safePage}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setCurrentPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setCurrentPage(1);
        }}
        labels={{
          show: '顯示',
          itemsUnit: '筆',
          range: (s, e) => `${s} - ${e} 筆`,
          prevPage: '上一頁',
          nextPage: '下一頁',
        }}
      />
    </Flex>
  );
};

export default IssuerTable;
